import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, FileText, Mail, Ticket, Calendar, CreditCard } from 'lucide-react';
import { SyncRecord, SyncSource, CHANGE_BADGE, SOURCES, PreviewPayload, ClassificationPayload, SummaryPayload, relativeTime } from './types';
import { RecordDiff } from './RecordDiff';
const SOURCE_ICONS: Record<SyncSource, React.FC<{
  size?: number;
  className?: string;
}>> = {
  confluence: FileText,
  gmail: Mail,
  jira: Ticket,
  gcal: Calendar,
  quickbooks: CreditCard
};
type TabId = 'preview' | 'diff' | 'classification' | 'summary';
interface Props {
  record: SyncRecord | null;
  onClose: () => void;
}
export function RecordSheet({
  record,
  onClose
}: Props) {
  const [tab, setTab] = useState<TabId>('preview');
  useEffect(() => {
    setTab('preview');
  }, [record?.id]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <AnimatePresence data-id="element-2026">
      {record && <motion.div key="record-sheet-backdrop" className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center" initial={{
      opacity: 0,
      pointerEvents: 'auto'
    }} animate={{
      opacity: 1,
      pointerEvents: 'auto'
    }} exit={{
      opacity: 0,
      pointerEvents: 'none'
    }} onClick={onClose} data-id="element-2027">
          <motion.div className="bg-[#141418] w-full max-w-6xl h-[92vh] rounded-t-[20px] border-t border-x border-white/[0.06] flex flex-col" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} onClick={e => e.stopPropagation()} data-id="element-2028">
            <Header record={record} onClose={onClose} data-id="element-2029" />
            <Tabs record={record} tab={tab} onChange={setTab} data-id="element-2030" />
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-2031">
              {tab === 'preview' && <PreviewTab preview={record.preview} data-id="element-2032" />}
              {tab === 'diff' && record.diff && <RecordDiff diff={record.diff} data-id="element-2033" />}
              {tab === 'classification' && record.classification && <ClassificationTab classification={record.classification} data-id="element-2034" />}
              {tab === 'summary' && record.summary && <SummaryTab summary={record.summary} data-id="element-2035" />}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Header({
  record,
  onClose
}: {
  record: SyncRecord;
  onClose: () => void;
}) {
  const Icon = SOURCE_ICONS[record.source];
  const sourceMeta = SOURCES.find(s => s.id === record.source);
  const sourceLabel = sourceMeta?.label ?? record.source;
  const badge = CHANGE_BADGE[record.changeType];
  return <div className="flex items-center justify-between px-6 pt-6 pb-4 gap-4 border-b border-white/[0.06]" data-id="element-2036">
      <div className="flex items-center gap-3 min-w-0" data-id="element-2037">
        <div className="w-10 h-10 rounded-[10px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-2038">
          <Icon size={18} className="text-[#8a8680]" data-id="element-2039" />
        </div>
        <div className="min-w-0" data-id="element-2040">
          <div className="text-[18px] font-medium text-[#e8e4df] truncate" data-id="element-2041">{record.title}</div>
          <div className="flex items-center gap-2 mt-1" data-id="element-2042">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#252530] text-[#8a8680]" data-id="element-2043">{sourceLabel}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${badge.classes}`} data-id="element-2044">
              {badge.label}
            </span>
            <span className="text-[11px] text-[#8a8680]" data-id="element-2045">arrived {relativeTime(record.arrivedAt)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0" data-id="element-2046">
        <a href={record.externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[13px] text-[#8a8680] hover:text-[#4a9a9a]" data-id="element-2047">
          <ExternalLink size={14} data-id="element-2048" />
          View in {sourceLabel}
        </a>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/5 rounded-lg" data-id="element-2049">
          <X size={18} className="text-[#8a8680]" data-id="element-2050" />
        </button>
      </div>
    </div>;
}
function Tabs({
  record,
  tab,
  onChange
}: {
  record: SyncRecord;
  tab: TabId;
  onChange: (t: TabId) => void;
}) {
  const visibleTabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'preview',
    label: 'Preview'
  }];
  if (record.diff) visibleTabs.push({
    id: 'diff',
    label: 'Diff'
  });
  if (record.classification) visibleTabs.push({
    id: 'classification',
    label: 'Classification'
  });
  if (record.summary) visibleTabs.push({
    id: 'summary',
    label: 'Summary'
  });
  return <nav className="flex items-center gap-6 px-6 border-b border-[#8a8680]/10" data-id="element-2051">
      {visibleTabs.map(({
      id,
      label
    }) => <button key={id} onClick={() => onChange(id)} className={`pb-3 pt-2 text-[14px] font-medium transition-colors relative ${tab === id ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-2052">
          {label}
          {tab === id && <motion.div layoutId="recordSheetTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
        type: 'spring',
        stiffness: 500,
        damping: 30
      }} data-id="element-2053" />}
        </button>)}
    </nav>;
}
function PreviewTab({
  preview
}: {
  preview: PreviewPayload;
}) {
  switch (preview.source) {
    case 'confluence':
      return <ConfluencePreview markdown={preview.markdown} data-id="element-2054" />;
    case 'gmail':
      return <GmailPreview from={preview.from} to={preview.to} subject={preview.subject} body={preview.body} data-id="element-2055" />;
    case 'jira':
      return <JiraPreview fields={preview.fields} description={preview.description} data-id="element-2056" />;
    case 'gcal':
      return <GcalPreview start={preview.start} end={preview.end} attendees={preview.attendees} location={preview.location} description={preview.description} data-id="element-2057" />;
    case 'quickbooks':
      return <QuickbooksPreview number={preview.number} customer={preview.customer} lineItems={preview.lineItems} total={preview.total} status={preview.status} data-id="element-2058" />;
  }
}
function ConfluencePreview({
  markdown
}: {
  markdown: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2059">
      <pre className="whitespace-pre-wrap text-[13px] text-[#c4bfb8] leading-relaxed font-sans" data-id="element-2060">{markdown}</pre>
    </div>;
}
function GmailPreview({
  from,
  to,
  subject,
  body
}: {
  from: string;
  to: string;
  subject: string;
  body: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2061">
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-2062">
        <span className="w-16 text-[#8a8680]" data-id="element-2063">From</span>
        <span className="text-[#e8e4df]" data-id="element-2064">{from}</span>
      </div>
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-2065">
        <span className="w-16 text-[#8a8680]" data-id="element-2066">To</span>
        <span className="text-[#e8e4df]" data-id="element-2067">{to}</span>
      </div>
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-2068">
        <span className="w-16 text-[#8a8680]" data-id="element-2069">Subject</span>
        <span className="text-[#e8e4df]" data-id="element-2070">{subject}</span>
      </div>
      <div className="mt-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-2071">{body}</div>
    </div>;
}
function JiraPreview({
  fields,
  description
}: {
  fields: {
    label: string;
    value: string;
  }[];
  description: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2072">
      <div className="grid grid-cols-2 gap-6" data-id="element-2073">
        <div data-id="element-2074">
          {fields.map((f, i) => <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-b-0" data-id="element-2075">
              <span className="text-[13px] text-[#8a8680]" data-id="element-2076">{f.label}</span>
              <span className="text-[13px] text-[#e8e4df] font-medium" data-id="element-2077">{f.value}</span>
            </div>)}
        </div>
        <div className="bg-[#252530] rounded-[12px] p-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-2078">
          {description}
        </div>
      </div>
    </div>;
}
function GcalPreview({
  start,
  end,
  attendees,
  location,
  description
}: {
  start: string;
  end: string;
  attendees: string[];
  location: string;
  description: string;
}) {
  const tiles: {
    label: string;
    value: string;
  }[] = [{
    label: 'Start',
    value: start
  }, {
    label: 'End',
    value: end
  }, {
    label: 'Location',
    value: location
  }, {
    label: 'Attendees',
    value: attendees.join(', ')
  }];
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2079">
      <div className="grid grid-cols-2 gap-3" data-id="element-2080">
        {tiles.map(t => <div key={t.label} className="bg-[#252530] rounded-[12px] p-4" data-id="element-2081">
            <div className="text-[11px] text-[#8a8680] uppercase tracking-wider" data-id="element-2082">{t.label}</div>
            <div className="text-[13px] text-[#e8e4df] mt-1" data-id="element-2083">{t.value}</div>
          </div>)}
      </div>
      <div className="mt-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-2084">{description}</div>
    </div>;
}
function QuickbooksPreview({
  number,
  customer,
  lineItems,
  total,
  status
}: {
  number: string;
  customer: string;
  lineItems: {
    label: string;
    amount: string;
  }[];
  total: string;
  status: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2085">
      <div className="flex items-center justify-between" data-id="element-2086">
        <div className="text-[16px] font-medium text-[#e8e4df]" data-id="element-2087">{number}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#4a9a9a]/20 text-[#4a9a9a] uppercase tracking-wider" data-id="element-2088">
          {status}
        </span>
      </div>
      <div className="text-[13px] text-[#8a8680] mt-1" data-id="element-2089">Customer: {customer}</div>
      <div className="mt-4" data-id="element-2090">
        {lineItems.map((li, i) => <div key={i} className="flex justify-between py-2 border-b border-white/[0.04] text-[13px]" data-id="element-2091">
            <span className="text-[#c4bfb8]" data-id="element-2092">{li.label}</span>
            <span className="text-[#e8e4df]" data-id="element-2093">{li.amount}</span>
          </div>)}
      </div>
      <div className="flex justify-between pt-4 text-[14px] font-medium text-[#e8e4df]" data-id="element-2094">
        <span data-id="element-2095">Total</span>
        <span data-id="element-2096">{total}</span>
      </div>
    </div>;
}
function ClassificationTab({
  classification
}: {
  classification: ClassificationPayload;
}) {
  const [reclassifying, setReclassifying] = useState(false);
  const handleReclassify = () => {
    setReclassifying(true);
    setTimeout(() => setReclassifying(false), 1500);
  };
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2097">
      <span className="inline-block px-3 py-1 rounded-full bg-[#4a9a9a]/20 text-[#4a9a9a] text-[13px] font-medium" data-id="element-2098">
        {classification.label}
      </span>
      <div className="flex items-center gap-3 mt-4" data-id="element-2099">
        <div className="flex-1 h-2 rounded-full bg-[#252530] overflow-hidden" data-id="element-2100">
          <div className="h-full bg-[#4a9a9a]" style={{
          width: `${classification.confidence * 100}%`
        }} data-id="element-2101" />
        </div>
        <span className="text-[12px] text-[#8a8680] shrink-0" data-id="element-2102">
          {Math.round(classification.confidence * 100)}%
        </span>
      </div>
      <p className="text-[12px] text-[#8a8680] mt-3" data-id="element-2103">
        classified by {classification.model} · {classification.latencyMs}ms
      </p>
      <p className="text-[14px] text-[#c4bfb8] leading-relaxed mt-4" data-id="element-2104">{classification.reasoning}</p>
      <button onClick={handleReclassify} className="mt-6 px-4 py-2 rounded-[10px] bg-[#252530] text-[#e8e4df] text-[13px] hover:bg-[#2a2a35] transition-colors disabled:opacity-50" disabled={reclassifying} data-id="element-2105">
        {reclassifying ? 'Reclassifying…' : 'Reclassify'}
      </button>
    </div>;
}
function SummaryTab({
  summary
}: {
  summary: SummaryPayload;
}) {
  return <div data-id="element-2106">
      <p className="text-[12px] text-[#8a8680] mb-4" data-id="element-2107">
        Indexed as {summary.chunkCount} chunks · {summary.embeddingModel}
      </p>
      <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-2108">
        <p className="text-[14px] text-[#c4bfb8] leading-relaxed" data-id="element-2109">{summary.summary}</p>
      </div>
      <a href="#" className="inline-flex items-center gap-1.5 mt-4 text-[13px] text-[#8a8680] hover:text-[#4a9a9a] transition-colors" data-id="element-2110">
        <ExternalLink size={14} data-id="element-2111" />
        Open full document
      </a>
    </div>;
}